import { zodResolver } from "@hookform/resolvers/zod";
import type { DeSoTokenMarketOrderWithFeeResponse } from "deso-protocol";
import {
  createDeSoTokenMarketOrderWithFee,
  CurrencyType,
  FillTypeWithFee,
  OperationTypeWithFee,
} from "deso-protocol";
import type { TxRequestOptions } from "deso-protocol/src/types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import zod from "zod";

import DesoAvatar from "@/components/shared/deso-avatar";
import { useDebounce } from "@/components/ui/multiselect";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import {
  UserContext,
  useUserAccountSignalState,
  useUserAuthSignalState,
  useUserBalancesSignalState,
} from "@/contexts/user-context";
import { useCCBalance } from "@/hooks/use-cc-balance";
import { useCreatorCoinFee } from "@/hooks/use-creator-coin-fee";
import { focusToUsd, nanosToInteger } from "@/utils/currency";
import { getUserProfilePicture, parseError } from "@/utils/helpers";
import type { Maybe, PartialAccount } from "@/utils/types";

interface CurrenciesList {
  displayName: string;
  ticker: string;
  icon: string;
  inputSuffix: string;
  avatar?: React.ReactNode;
}

export const useTradeCreatorCoin = ({
  account,
  operationType,
  orderType = "market",
  showErrorToast = true,
}: {
  account: Maybe<PartialAccount>;
  operationType: OperationTypeWithFee;
  orderType?: "market" | "limit";
  showErrorToast?: boolean;
}) => {
  const { refetchUser } = useContext(UserContext);
  const { user } = useUserAccountSignalState();
  const { currentUserPublicKey } = useUserAuthSignalState();
  const { realTimeFocusUnlockedBalanceNanos } = useUserBalancesSignalState();
  const {
    loading: loadingCreatorCoinFees,
    founderRewardFeePercentage,
    creatorCoinMarketFee,
  } = useCreatorCoinFee({
    publicKey: account?.publicKey || "",
    username: account?.username || "",
  });

  const { focusExchangeRate, loading: loadingFocusExchangeRate } =
    useFocusMarketData();
  const [transactionPreview, setTransactionPreview] =
    useState<DeSoTokenMarketOrderWithFeeResponse | null>(null);
  const {
    ccBalanceNanos,
    loading: loadingCCBalance,
    refetch: refetchCCBalance,
  } = useCCBalance(account, currentUserPublicKey);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const AVAILABLE_CURRENCIES = useMemo(() => {
    return [
      {
        displayName: "USD",
        ticker: "DUSD",
        icon: "/assets/currencies/DUSD.png",
        inputSuffix: "$",
      },
      {
        displayName: "FOCUS",
        ticker: "FOCUS",
        icon: "/assets/logo-focus-mark.svg", // TODO: Ask Ed for a logo
        inputSuffix: "",
      },
      account && {
        displayName: account.username,
        ticker: account.username,
        icon: getUserProfilePicture(account),
        inputSuffix: "",
        avatar: <DesoAvatar account={account} size={22} />,
      },
    ].filter(Boolean) as Array<CurrenciesList>;
  }, [account]);

  const FormSchema = zod
    .object({
      depositCurrency: zod.string({
        error: "Please select a currency to use.",
      }),
      amount: zod
        .number({ error: "Please enter a valid amount" })
        .min(0.00001, { message: "Number must be at least 0.00001" }),
      priceCurrency: zod.string({
        error: "Please select a currency to use.",
      }),
      price: zod
        .number({ error: "Please enter a valid amount" })
        .min(0, { message: "Number must not be negative" }),
    })
    .refine(
      ({ amount, depositCurrency }) => {
        if (operationType === OperationTypeWithFee.BID && priceWatch === 0) {
          switch (depositCurrency) {
            case "DUSD":
              return (
                !loadingFocusExchangeRate &&
                focusExchangeRate &&
                amount <=
                  nanosToInteger(
                    focusToUsd(
                      realTimeFocusUnlockedBalanceNanos,
                      focusExchangeRate,
                    ),
                  )
              );
            case "FOCUS":
              return (
                amount <= nanosToInteger(realTimeFocusUnlockedBalanceNanos)
              );
            default:
              // TODO: handle it properly
              return true;
          }
        } else {
          // TODO: not sure how to handle field validation for sell
          return true;
        }
      },
      {
        message: "The amount exceeds your available balance.",
        path: ["amount"],
      },
    )
    .refine(
      ({ price }) => {
        if (orderType === "limit") {
          return price > 0;
        }
        return true;
      },
      {
        message: "Price must be greater than 0 for limit orders.",
        path: ["price"],
      },
    );

  const form = useForm<zod.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      depositCurrency: AVAILABLE_CURRENCIES[0].ticker,
      amount: 0,
      price: 0,
      priceCurrency: AVAILABLE_CURRENCIES[0].ticker,
    },
    mode: "onChange",
  });

  const formValid = form.formState.isValid;
  const amountWatch = form.watch("amount");
  const depositCurrencyWatch = form.watch("depositCurrency");
  const priceWatch = form.watch("price");
  const priceCurrencyWatch = form.watch("priceCurrency");

  const getInputSuffix = useCallback(
    (ticker: string) => {
      const item = AVAILABLE_CURRENCIES.find((e) => e.ticker === ticker);
      return item?.inputSuffix || "";
    },
    [AVAILABLE_CURRENCIES],
  );

  const debouncedAmountWatch = useDebounce(amountWatch, 500);
  const debouncedPriceWatch = useDebounce(priceWatch, 500);

  const quantityCurrencyType = (currency: string) => {
    switch (currency) {
      case "DUSD":
        return CurrencyType.usd;
      case "FOCUS":
        return CurrencyType.quote;
      case account?.username:
      default:
        return CurrencyType.base;
    }
  };

  const onTransactionSubmitted = async () => {
    if (!user) {
      return;
    }

    try {
      await Promise.all([refetchUser(), refetchCCBalance()]);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when refreshing your balance`,
        details: createToastErrorDetails(e),
      });
    }
  };

  const prepareTransaction = (options?: TxRequestOptions) => {
    if (!user) {
      return Promise.reject("Current user is not logged in.");
    }

    const accountPublicKey = account?.publicKey;

    if (!accountPublicKey) {
      return Promise.reject("Account is required");
    }

    return createDeSoTokenMarketOrderWithFee(
      {
        TransactorPublicKeyBase58Check: currentUserPublicKey,
        QuoteCurrencyPublicKeyBase58Check:
          process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY || "",
        BaseCurrencyPublicKeyBase58Check: accountPublicKey,
        OperationType: operationType as unknown as OperationTypeWithFee, // TODO: not commit
        FillType:
          orderType === "limit"
            ? FillTypeWithFee.GOOD_TILL_CANCELLED
            : FillTypeWithFee.IMMEDIATE_OR_CANCEL,
        Price: priceWatch.toString(),
        PriceCurrencyType: quantityCurrencyType(priceCurrencyWatch),
        Quantity: amountWatch.toString(),
        QuantityCurrencyType: quantityCurrencyType(depositCurrencyWatch),
        MinFeeRateNanosPerKB: 0,
        TransactionFees: null,
        OptionalPrecedingTransactions: null,
      },
      options,
    );
  };

  useEffect(() => {
    const fetchTransactionPreview = async () => {
      if (!form.formState.isDirty) {
        return;
      }

      const isFormValid = await form.trigger();

      setErrorMessage(null);

      if (!user || !isFormValid || !account) {
        setTransactionPreview(null);
        return;
      }

      try {
        const { constructedTransactionResponse } = await prepareTransaction({
          checkPermissions: false,
          broadcast: false,
        });
        setTransactionPreview(constructedTransactionResponse);
      } catch (e: unknown) {
        setTransactionPreview(null);

        if (
          e &&
          typeof e === "object" &&
          "message" in e &&
          typeof e.message === "string" &&
          (e.message
            .toLowerCase()
            .includes("is not enough to cover the amount") ||
            e.message
              .toLowerCase()
              .includes("insufficient balance to open order"))
        ) {
          setErrorMessage(
            "Error: The entered amount exceeds your available balance.",
          );

          if (showErrorToast) {
            toast({
              variant: "destructive",
              title: "Error",
              description: `An error happened when previewing your order - The entered amount exceeds your available balance.`,
            });
          }

          return;
        }

        setErrorMessage(`Error: ${parseError(e)}`);

        if (showErrorToast) {
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when previewing your order`,
            details: createToastErrorDetails(e),
          });
        }

        setTransactionPreview(null);
      }
    };

    fetchTransactionPreview();
  }, [
    account,
    currentUserPublicKey,
    debouncedAmountWatch,
    debouncedPriceWatch,
    depositCurrencyWatch,
    focusExchangeRate,
    form,
    loadingFocusExchangeRate,
    operationType,
    orderType,
    priceCurrencyWatch,
    realTimeFocusUnlockedBalanceNanos,
    showErrorToast,
    user,
  ]);

  return {
    formValid,
    depositCurrencyWatch,
    form,
    onTransactionSubmitted,
    founderRewardFeePercentage,
    loadingCreatorCoinFees,
    ccAvailableCurrencies: AVAILABLE_CURRENCIES,
    transactionPreview,
    ccBalanceNanos,
    loadingCCBalance,
    prepareTransaction,
    depositCurrencyInputSuffix: getInputSuffix(depositCurrencyWatch),
    priceInputSuffix: getInputSuffix(priceCurrencyWatch),
    creatorCoinMarketFee,
    errorMessage,
  };
};
